import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import { useStore } from "@/models";
import FiatCurrencyIcon from "../FiatCurrencyIcon";
import {
  formatConvertBalanceApproxDisplay,
  formatConvertBalanceDisplay,
} from "../convertBalanceUtils";
import shellStyles from "../convertBottomSheet.module.less";
import { useConvertBottomSheet } from "../useConvertBottomSheet";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

function findOption(options, code) {
  const upper = (code || "").toUpperCase();
  return options.find((o) => o.code === upper) || { code: upper, name: upper, color: "#26A69A" };
}

function SegmentCurrency({ code, options }) {
  const opt = findOption(options, code);
  return (
    <>
      <FiatCurrencyIcon
        code={opt.code}
        className={styles.segmentIcon}
        style={{ background: opt.color || "#26A69A" }}
      />
      <span className={styles.segmentCode}>{opt.code}</span>
    </>
  );
}

/**
 * Figma Actionsheet 15298:58797 — From/To 币种选择抽屉
 */
export default observer(function ConvertCurrencyDrawer({
  open,
  initialSide = "from",
  fromCurrency,
  toCurrency,
  fromOptions = [],
  toOptions = [],
  fromBalanceMap = null,
  showFromBalances = false,
  onClose,
  onFromChange,
  onToChange,
}) {
  const t = useTranslation();
  const { fund } = useStore();
  const [activeSide, setActiveSide] = useState(initialSide);

  const {
    visible,
    sheetRef,
    dragZoneRef,
    closeWithAnimation,
    handleSheetAnimationEnd,
    overlayClassName,
    backdropClassName,
    sheetClassName,
    backdropStyle,
    sheetStyle,
  } = useConvertBottomSheet({ open, onClose, styles: shellStyles });

  useEffect(() => {
    if (open) setActiveSide(initialSide);
  }, [open, initialSide]);

  useEffect(() => {
    if (!open || !showFromBalances) return;
    if (!fund?.assist?.key) return;
    if (fund.assistPriceMap && Object.keys(fund.assistPriceMap).length > 0) return;
    fund.fetchAssistPriceMap?.();
  }, [open, showFromBalances, fund]);

  const listOptions = activeSide === "from" ? fromOptions : toOptions;

  const handleSelect = (code) => {
    if (activeSide === "from") {
      onFromChange?.(code);
      setActiveSide("to");
      return;
    }
    onToChange?.(code);
    closeWithAnimation();
  };

  const fromOpt = useMemo(() => findOption(fromOptions, fromCurrency), [fromOptions, fromCurrency]);
  const toOpt = useMemo(() => findOption(toOptions, toCurrency), [toOptions, toCurrency]);

  if (!visible || typeof document === "undefined") return null;

  return createPortal(
    <div className={overlayClassName} onClick={closeWithAnimation} role="presentation">
      <div className={backdropClassName} style={backdropStyle} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={`${sheetClassName} ${styles.sheetShell}`}
        style={sheetStyle}
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={activeSide === "from" ? t("convert.from") : t("convert.to")}
      >
        <div className={shellStyles.dragZone}>
          <div ref={dragZoneRef} className={shellStyles.topHandle} aria-hidden="true" />

          <div className={styles.segmentWrap}>
            <div className={styles.segment}>
              <button
                type="button"
                className={`${styles.segmentTab} ${activeSide === "from" ? styles["segmentTab--active"] : ""}`}
                onClick={() => setActiveSide("from")}
              >
                <span className={styles.segmentLabel}>{t("convert.from")}</span>
                <div className={styles.segmentCurrency}>
                  <SegmentCurrency code={fromCurrency} options={fromOptions.length ? fromOptions : [fromOpt]} />
                </div>
              </button>
              <button
                type="button"
                className={`${styles.segmentTab} ${activeSide === "to" ? styles["segmentTab--active"] : ""}`}
                onClick={() => setActiveSide("to")}
              >
                <span className={styles.segmentLabel}>{t("convert.to")}</span>
                <div className={styles.segmentCurrency}>
                  <SegmentCurrency code={toCurrency} options={toOptions.length ? toOptions : [toOpt]} />
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.list} role="listbox">
            {listOptions.map((opt) => {
              const balanceEntry = fromBalanceMap?.[opt.code];
              const approxLabel =
                activeSide === "from" && showFromBalances && balanceEntry
                  ? formatConvertBalanceApproxDisplay(balanceEntry, opt.code, fund)
                  : null;

              return (
              <button
                key={opt.code}
                type="button"
                role="option"
                aria-selected={
                  activeSide === "from"
                    ? fromCurrency === opt.code
                    : toCurrency === opt.code
                }
                className={styles.listItem}
                onClick={() => handleSelect(opt.code)}
              >
                <FiatCurrencyIcon
                  code={opt.code}
                  className={styles.listIcon}
                  style={{ background: opt.color || "#26A69A" }}
                />
                <div className={styles.listText}>
                  <span className={styles.listCode}>{opt.code}</span>
                  <span className={styles.listName}>{opt.name || opt.code}</span>
                </div>
                {activeSide === "from" && showFromBalances && fromBalanceMap && (
                  <div className={styles.listBalance}>
                    <span className={styles.listBalanceAmount}>
                      {formatConvertBalanceDisplay(balanceEntry ?? "0", opt.isFiat)}
                    </span>
                    {approxLabel && (
                      <span className={styles.listBalanceConvert}>{approxLabel}</span>
                    )}
                  </div>
                )}
              </button>
            );
            })}
          </div>
        </div>

        <div className={styles.bottomHandle} aria-hidden="true" />
      </div>
    </div>,
    document.body
  );
});
